import React from 'react';

/**
 * EmptyState - Estado vacío reutilizable
 * Para mostrar cuando no hay datos o contenido
 */
export function EmptyState({ 
  icon: Icon, 
  title, 
  subtitle, 
  primary, 
  secondary,
  className = '' 
}) {
  return (
    <div className={`rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.6)] p-8 text-center ${className}`}>
      {Icon && (
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[rgba(var(--brand),0.12)] flex items-center justify-center">
          <Icon className="h-5 w-5 text-[rgb(var(--brand))]" />
        </div>
      )}
      
      <h3 className="text-base font-medium text-[rgb(var(--fg))]">
        {title}
      </h3>
      
      {subtitle && (
        <p className="mt-1 text-sm text-[rgb(var(--muted))] max-w-sm mx-auto">
          {subtitle}
        </p>
      )}
      
      {(primary || secondary) && (
        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
          {primary}
          {secondary}
        </div>
      )}
    </div>
  );
}

/**
 * SkeletonRow - Loader skeleton para filas
 */
export function SkeletonRow({ className = '' }) {
  return (
    <div className={`h-10 w-full animate-pulse rounded-xl bg-[rgb(var(--subpanel))] ${className}`} />
  );
}

/**
 * SkeletonCard - Loader skeleton para cards
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={`rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.6)] p-6 ${className}`}>
      <div className="animate-pulse space-y-4">
        {/* Header */}
        <div className="h-4 bg-[rgb(var(--subpanel))] rounded-lg w-3/4"></div>
        <div className="h-3 bg-[rgb(var(--subpanel))] rounded w-1/2"></div>
        
        {/* Content */}
        <div className="space-y-3 pt-2">
          <div className="h-3 bg-[rgb(var(--subpanel))] rounded"></div>
          <div className="h-3 bg-[rgb(var(--subpanel))] rounded w-5/6"></div>
          <div className="h-3 bg-[rgb(var(--subpanel))] rounded w-4/6"></div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <div className="h-8 bg-[rgb(var(--subpanel))] rounded-lg w-20"></div>
          <div className="h-8 bg-[rgb(var(--subpanel))] rounded-lg w-16"></div>
        </div>
      </div>
    </div>
  );
}

/**
 * SkeletonTable - Loader skeleton para tablas
 */
export function SkeletonTable({ rows = 5, columns = 4, className = '' }) {
  return (
    <div className={`rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.6)] overflow-hidden ${className}`}>
      {/* Header */}
      <div className="border-b border-[rgb(var(--border))] p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, i) => (
            <div key={i} className="h-4 bg-[rgb(var(--subpanel))] rounded animate-pulse"></div>
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-[rgb(var(--border))]">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="p-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {Array.from({ length: columns }).map((_, j) => (
                <div key={j} className="h-3 bg-[rgb(var(--subpanel))] rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * SkeletonForm - Loader skeleton para formularios
 */
export function SkeletonForm({ fields = 6, className = '' }) {
  return (
    <div className={`rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.6)] p-6 ${className}`}>
      <div className="animate-pulse">
        {/* Title */}
        <div className="h-6 bg-[rgb(var(--subpanel))] rounded-lg w-1/3 mb-6"></div>
        
        {/* Form fields */}
        <div className="grid grid-cols-12 gap-4">
          {Array.from({ length: fields }).map((_, i) => (
            <div key={i} className="col-span-12 md:col-span-6 space-y-2">
              <div className="h-3 bg-[rgb(var(--subpanel))] rounded w-1/4"></div>
              <div className="h-10 bg-[rgb(var(--subpanel))] rounded-xl"></div>
            </div>
          ))}
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <div className="h-10 bg-[rgb(var(--subpanel))] rounded-xl w-20"></div>
          <div className="h-10 bg-[rgb(var(--subpanel))] rounded-xl w-24"></div>
        </div>
      </div>
    </div>
  );
}